import { InputAdornment, outlinedInputClasses } from '@mui/material';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { useEffect, useState, forwardRef } from 'react';
import { TextInput, TextInputProps } from './TextInput';

export type DebouncedInputProps = Omit<TextInputProps, 'onChange'> & {
  onChange: (value: string) => void;
  value: string;
  debounce?: number;
  placeholder: string;
  autoFocus?: boolean;
  onClear?: () => void;
};

export const DebouncedInput = forwardRef<HTMLInputElement, DebouncedInputProps>(
  (
    {
      value: initialValue,
      onChange,
      debounce = 300,
      placeholder,
      onClear,
      ...props
    }: DebouncedInputProps,
    ref
  ) => {
    const [value, setValue] = useState<string>(initialValue);
    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
      const timeout = setTimeout(() => {
        onChange(value);
      }, debounce);
      return () => clearTimeout(timeout);
    }, [value]);

    return (
      <TextInput
        variant="outlined"
        {...props}
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder={placeholder}
        sx={{
          width: '320px',
          ...props?.sx,
          [`& .${outlinedInputClasses.root}`]: { paddingLeft: '10px' },
          input: {
            ...(props?.sx as unknown as Record<string, object>)?.input,
            paddingLeft: '0px',
          },
        }}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment sx={{ mr: '0' }} position="start">
              <MagnifyingGlass size={20} style={{ marginRight: '8px' }} />
            </InputAdornment>
          ),
          endAdornment:
            onClear && value ? (
              <InputAdornment sx={{ mr: '0' }} position="end" onClick={() => onClear()}>
                <X size={16} style={{ marginRight: '8px', cursor: 'pointer' }} />
              </InputAdornment>
            ) : undefined,
        }}
        ref={ref}
      />
    );
  }
);

DebouncedInput.displayName = 'DebouncedInput';
